'use client'

import { useEffect, useState } from 'react'
import { motion, animate } from 'motion/react'
import { TrendingUp, Flame, Star } from 'lucide-react'
import { useMk8Player } from '@/hooks/use-mk8-player'

const TARGET_MMR = 8420
const TIER_COLOR = '#00eaff'

function getDivisionProgress(mmr: number, tiers: any[] = []) {
  const sorted = [...tiers].sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
  const cur = sorted.find((t) => mmr >= Number(t.minMmr) && mmr <= Number(t.maxMmr))
  if (!cur) {
    return { pct: 78, left: '', right: 'ULTRA-CHAMPIONS · 7500+' }
  }

  const divisions = Math.max(1, Number(cur.divisions || 5))
  const min = Number(cur.minMmr)
  const max = Number(cur.maxMmr)
  const span = Math.max(1, max - min + 1)
  const step = Math.max(1, Math.floor(span / divisions))
  const rawIdx = Math.floor((mmr - min) / step)
  const idx = Math.max(0, Math.min(divisions - 1, rawIdx))

  const divMin = min + idx * step
  const divMax = idx === divisions - 1 ? max : min + (idx + 1) * step - 1
  const pct = Math.max(0, Math.min(100, ((mmr - divMin) / Math.max(1, divMax - divMin + 1)) * 100))

  const rom = ['I', 'II', 'III', 'IV', 'V']
  const left = `${cur.name} ${rom[idx] || idx + 1} · ${divMin}`

  let right = ''
  if (idx < divisions - 1) {
    right = `${cur.name} ${rom[idx + 1] || idx + 2} · ${divMax + 1}`
  } else {
    const next = sorted.find((t) => Number(t.order) === Number(cur.order) + 1)
    right = next ? `${next.name} I · ${Number(next.minMmr)}` : `${cur.name} MAX`
  }

  return { pct, left, right }
}

function useCountUp(target: number, duration: number = 2.4) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    const timeout = setTimeout(() => {
      const controls = animate(0, target, {
        duration,
        ease: [0.16, 1, 0.3, 1],
        onUpdate(v) {
          setValue(Math.round(v))
        },
      })
      return () => controls.stop()
    }, 600)
    return () => clearTimeout(timeout)
  }, [target, duration])

  return value
}

export function Hero({ profileId }: { profileId?: string }) {
  const { myStats, rank, summary } = useMk8Player(profileId)
  const targetMmr = myStats?.mmr || TARGET_MMR
  const mmr = useCountUp(targetMmr)
  const [glowing, setGlowing] = useState(false)
  const tier = myStats?.rankLabel || myStats?.rankName || 'Iron I'
  const progress = getDivisionProgress(targetMmr, summary?.rankTiers || [])
  const rankTiers = [...(summary?.rankTiers || [])].sort(
    (a, b) => Number(a.order || 0) - Number(b.order || 0),
  )

  useEffect(() => {
    const t = setTimeout(() => setGlowing(true), 3100)
    return () => clearTimeout(t)
  }, [])

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#08080e' }}
    >
      {/* Speed line background */}
      <SpeedLines />

      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 50% 55%, rgba(0,234,255,0.07) 0%, transparent 70%)',
        }}
      />

      {/* Noise overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '256px 256px',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-4xl mx-auto">
        <motion.img
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          src="/assets/branding/mk8d-stats-logo-v2.png"
          alt="MK8D Stats"
          style={{ width: 'min(140px, 28vw)', height: 'auto', marginBottom: 16 }}
        />

        {/* Tier badge */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-2 mb-8 px-5 py-2 rounded-full border"
          style={{
            borderColor: 'rgba(0,234,255,0.3)',
            background: 'rgba(0,234,255,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Star size={13} fill={TIER_COLOR} color={TIER_COLOR} />
          <span
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: TIER_COLOR,
              fontSize: '12px',
            }}
          >
            {tier} RANK · {(summary?.currentSeason?.name || 'Season 0 · Prelude').toUpperCase()}
          </span>
          <Star size={13} fill={TIER_COLOR} color={TIER_COLOR} />
        </motion.div>

        {/* Rank ladder */}
        {rankTiers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.5 }}
            className="mb-6 w-full max-w-4xl self-stretch"
          >
            <details
              className="rounded-2xl px-4 py-2 w-full"
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <summary
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: '11px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                className="py-1"
              >
                RANK LADDER · DIVISIONS (open)
              </summary>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                {rankTiers.map((rt: any) => {
                  const divisions = Math.max(1, Number(rt.divisions || 5))
                  const divLabels = ['I', 'II', 'III', 'IV', 'V']
                    .slice(0, divisions)
                    .join(' · ')

                  return (
                    <div
                      key={rt.rankId}
                      className="rounded-xl px-3 py-2 text-left"
                      style={{
                        background:
                          myStats?.rankId === rt.rankId
                            ? 'rgba(0,234,255,0.12)'
                            : 'rgba(255,255,255,0.03)',
                        border:
                          myStats?.rankId === rt.rankId
                            ? '1px solid rgba(0,234,255,0.35)'
                            : '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      <p
                        style={{
                          fontFamily: "'Barlow Condensed', sans-serif",
                          fontWeight: 700,
                          fontSize: '13px',
                          color: '#fff',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {rt.name}
                      </p>
                      <p
                        style={{
                          fontFamily: "'Barlow', sans-serif",
                          fontSize: '11px',
                          color: 'rgba(255,255,255,0.45)',
                        }}
                      >
                        {Number(rt.minMmr)} → {Number(rt.maxMmr)}
                      </p>
                      <p
                        style={{
                          fontFamily: "'Barlow Condensed', sans-serif",
                          fontSize: '10px',
                          color: 'rgba(0,234,255,0.8)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        DIV {divLabels}
                      </p>
                    </div>
                  )
                })}
              </div>
            </details>
          </motion.div>
        )}

        {/* MMR display */}
        <div className="relative mb-4">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 600,
              letterSpacing: '0.3em',
              color: 'rgba(255,255,255,0.35)',
              fontSize: '13px',
            }}
            className="mb-3"
          >
            MATCHMAKING RATING
          </motion.p>

          <motion.div
            animate={
              glowing
                ? {
                    textShadow: [
                      '0 0 40px rgba(0,234,255,0.6)',
                      '0 0 80px rgba(0,234,255,0.9)',
                      '0 0 40px rgba(0,234,255,0.6)',
                    ],
                  }
                : {}
            }
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              fontFamily: "'Russo One', sans-serif",
              fontSize: 'clamp(96px, 18vw, 180px)',
              lineHeight: 1,
              color: '#fff',
              letterSpacing: '-0.02em',
            }}
          >
            {mmr.toLocaleString()}
          </motion.div>

          {/* MMR bar underneath */}
          <div
            className="mt-6 relative h-1.5 rounded-full overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.07)',
              width: '100%',
              maxWidth: 480,
              margin: '24px auto 0',
            }}
          >
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: `${progress.pct}%` }}
              transition={{
                delay: 0.8,
                duration: 2.2,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ background: 'linear-gradient(90deg, #0077ff, #00eaff)' }}
            />
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: `${progress.pct}%` }}
              transition={{
                delay: 0.8,
                duration: 2.2,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="absolute inset-y-0 left-0 rounded-full blur-sm opacity-70"
              style={{ background: 'linear-gradient(90deg, #0077ff, #00eaff)' }}
            />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.8 }}
            className="mt-2 flex justify-between"
            style={{
              maxWidth: 480,
              margin: '8px auto 0',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '11px',
              color: 'rgba(255,255,255,0.3)',
              letterSpacing: '0.1em',
            }}
          >
            <span>{progress.left || tier}</span>
            <span>{progress.right || 'ULTRA-CHAMPIONS · 7500+'}</span>
          </motion.div>
        </div>

        {/* Sub stats row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-8 mt-10"
        >
          {[
            {
              label: 'AVG POINTS/RACE',
              value: myStats ? myStats.avg.toFixed(2) : '67.3%',
              color: '#00eaff',
              icon: TrendingUp,
            },
            {
              label: 'RANK',
              value: rank >= 0 ? `#${rank + 1}` : '+340 MMR',
              color: '#ffe600',
              icon: Flame,
            },
            { label: 'PEAK MMR', value: targetMmr.toLocaleString(), color: '#ff3544', icon: Star },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center gap-1">
              <Icon size={16} color={color} />
              <span
                style={{
                  fontFamily: "'Russo One', sans-serif",
                  color: '#fff',
                  fontSize: '18px',
                }}
              >
                {value}
              </span>
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '10px',
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '10px',
              letterSpacing: '0.25em',
              color: 'rgba(255,255,255,0.25)',
            }}
          >
            SCROLL TO EXPLORE
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-px h-8"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,234,255,0.6), transparent)',
            }}
          />
        </motion.div>
      </div>
    </section>
  )
}

function SpeedLines() {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
    >
      {Array.from({ length: 28 }).map((_, i) => {
        const y = (i / 27) * 900
        const len = 200 + Math.random() * 800
        const x = Math.random() * 1440
        return (
          <line
            key={i}
            x1={x}
            y1={y}
            x2={x + len}
            y2={y}
            stroke="white"
            strokeWidth={i % 5 === 0 ? 2 : 1}
            strokeDasharray={i % 3 === 0 ? '8 4' : '0'}
          />
        )
      })}
    </svg>
  )
}
